/**
 * Генерирует изображение сертификата на основе шаблона
 * @param certificateNumber - Номер сертификата для отображения справа снизу
 * @param initialBalance - Начальный баланс для отображения по центру
 * @returns Promise с Blob изображения
 */
export async function generateCertificateImage(
  certificateNumber: string,
  initialBalance: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Не удалось получить контекст canvas'));
      return;
    }

    // Размеры шаблона
    const width = 1580;
    const height = 750;
    canvas.width = width;
    canvas.height = height;

    // Загружаем изображение шаблона
    const templateImage = new Image();
    templateImage.crossOrigin = 'anonymous';

    templateImage.onload = async () => {
      try {
        // Рисуем шаблон
        ctx.drawImage(templateImage, 0, 0, width, height);

        // Загружаем шрифт
        const fontFace = new FontFace(
          'BebasNeue',
          'url(/BebasNeue-Regular.ttf)'
        );

        await fontFace.load();
        document.fonts.add(fontFace);
        
        // Ждем, пока шрифт будет готов к использованию
        await document.fonts.ready;

        // Начальный баланс по центру (красный, 130px)
        const balanceText = Math.round(initialBalance).toString();
        ctx.font = '130px BebasNeue';
        ctx.fillStyle = '#DC2626'; // red-600
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(balanceText, width / 2 + 116, height / 2 + 83);

        // Номер сертификата справа снизу (белый, 36px)
        ctx.font = '36px BebasNeue';
        ctx.fillStyle = '#FFFFFF'; // белый
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        // Отступы от края: 20px справа, 20px снизу
        ctx.fillText(certificateNumber, width - 20, height - 20);

        // Конвертируем canvas в blob
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Не удалось создать изображение'));
          }
        }, 'image/png');
      } catch (error) {
        reject(error);
      }
    };

    templateImage.onerror = () => {
      reject(new Error('Не удалось загрузить шаблон изображения'));
    };

    // Загружаем изображение
    templateImage.src = '/gift.png';
  });
}

/**
 * Скачивает изображение сертификата
 * @param certificateNumber - Номер сертификата
 * @param initialBalance - Начальный баланс сертификата
 * @param fileName - Имя файла для скачивания (по умолчанию: certificate-{number}.png)
 */
export async function downloadCertificateImage(
  certificateNumber: string,
  initialBalance: number,
  fileName?: string
): Promise<void> {
  try {
    const blob = await generateCertificateImage(certificateNumber, initialBalance);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || `certificate-${certificateNumber}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Ошибка при генерации изображения сертификата:', error);
    throw error;
  }
}

